import React, { useState, useMemo, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { useRequestData } from '../../../Utilities/hooks'
import { SET_TRADEMARK_HISTORY, SET_RECORD_DETAILS } from '../RecordDetailsReducer'
import { editNote, deleteNote, saveNewNote, getEntityNotes } from '../RecordDetailsActions'
import NotesPanel from '../NotesPanel'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import { Body } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import * as api from '../../../requests'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton'
import ContractorBusinessPanel from './ContractorBusinessPanel'
import ContractorComplaintsPanel, { ContractorComplaintDetail } from './ContractorComplaintsPanel'
import { FilingSourceTypeIds } from '../../../Utilities/Utilities'
import { Drawer } from '../../Shell/Shell'

const ContractorRecordDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const route = 'ContractorRecordDetails'
  const { recordDetails, recordAmendmentHistory, recordHistoryList } = useSelector(
    (state) => state.record,
    shallowEqual,
  )
  const RecordDetailsSettings = useRequestData(
    (cb, ecb) => api.GET_RECORD_DETAIL_SETTING_BY_TYPE(route, cb, ecb),
    [],
    {},
  )
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)

  const contractorNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    setLoading(true)
    api.FETCH_RECORD_DETAILS_BY_ROUTE(
      route,
      contractorNum,
      (record) => {
        dispatch({ type: SET_RECORD_DETAILS, record: record })
        dispatch({ type: SET_TRADEMARK_HISTORY, history: record.history })
        setLoading(false)
      },
      (err) => {
        console.error(err)
        setLoading(false)
      },
    )
  }, [contractorNum])

  useEffect(() => {
    if (recordDetails.ContractorRecordId)
      dispatch(getEntityNotes(FilingSourceTypeIds.Contractor, recordDetails.ContractorRecordId))
  }, [recordDetails.ContractorRecordId])

  const isUsingBusinessExtension = useMemo(() => {
    const { Tabs } = RecordDetailsSettings
    if (!Tabs) return false
    return !!(Tabs || []).find((tab) => tab.Title === 'Business' && tab.IsEnabled)
  }, [RecordDetailsSettings])

  const ComplaintsTitle = useMemo(() => {
    const { Tabs } = RecordDetailsSettings
    if (Array.isArray(Tabs) && Tabs.length > 2) return Tabs[2].Title
    return 'Complaints'
  }, [RecordDetailsSettings])

  return (
    <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
      <Body className="trademark-record-details" showDrawer={showDrawer}>
        <div className="row header-row">
          <h1>{RecordDetailsSettings.Title || 'Contractor License Details'}</h1>
          <div className="tablist" role="tablist">
            <TabButton id="tab-1" forPanelId="panel-1" label="History" />
            <TabButton
              id="tab-2"
              forPanelId="panel-2"
              label="Notes"
              // customCss={
              //   recordDetails['SeeNotesFlag'] === true
              //     ? { backgroundColor: 'red !important', color: '#fff !important' }
              //     : {}
              // }
            />
            <TabButton id="tab-3" forPanelId="panel-3" label={ComplaintsTitle} />
            {!loading && isUsingBusinessExtension ? (
              <TabButton id="tab-4" forPanelId="panel-4" label="Business" />
            ) : null}
          </div>
        </div>
        {loading && (
          <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
            <Spinner className="page-loader" themeColor="dark-neutral" />
          </div>
        )}

        {!loading && <GenericDetails record={recordDetails} config={RecordDetailsSettings || {}} />}

        <div>
          {!loading && (
            <TabPanel tabId="tab-1" id="panel-1">
              <RecordHistory
                history={{
                  amendments: recordAmendmentHistory,
                  history: recordHistoryList,
                  template: !!RecordDetailsSettings.HistoryConfig
                    ? RecordDetailsSettings.HistoryConfig.filter((t) => t.IsEnabled)
                    : [],
                }}
                requiredVoidRight="ContractorVoid"
                module="contractor"
              />
            </TabPanel>
          )}
          <TabPanel tabId="tab-2" id="panel-2">
            <NotesPanel
              saveNote={(noteText) => {
                dispatch(
                  saveNewNote(
                    noteText,
                    FilingSourceTypeIds.Contractor,
                    recordDetails.ContractorRecordId,
                  ),
                )
              }}
              editNote={(note) => {
                dispatch(
                  editNote(note, FilingSourceTypeIds.Contractor, recordDetails.ContractorRecordId),
                )
              }}
              deleteNote={(noteId) => {
                dispatch(
                  deleteNote(
                    noteId,
                    FilingSourceTypeIds.Contractor,
                    recordDetails.ContractorRecordId,
                  ),
                )
              }}
            />
          </TabPanel>
          <TabPanel tabId="tab-3" id="panel-3">
            <ContractorComplaintsPanel
              setShowDrawer={setShowDrawer}
              config={RecordDetailsSettings}
            />
          </TabPanel>
          {!loading && isUsingBusinessExtension ? (
            <TabPanel tabId="tab-4" id="panel-4">
              <ContractorBusinessPanel config={RecordDetailsSettings} record={recordDetails} />
            </TabPanel>
          ) : null}
        </div>
        <RecordDetailPageBackButton />
        <Drawer
          showCloseButton
          show={showDrawer}
          title={'Complaint Details'}
          onSidebarDismiss={() => {
            setShowDrawer(false)
          }}
        >
          <ContractorComplaintDetail config={RecordDetailsSettings}/>
        </Drawer>
      </Body>
    </TabProvider>
  )
}

export default ContractorRecordDetails
